import { CommonModule } from '@angular/common'
import { Component, Input, OnDestroy, OnInit } from '@angular/core'
import { ActivatedRoute } from '@angular/router'
import { Assignee } from '@app/modules/workspaces/models/assignee.interface'
import { EventService as EventServiceV2 } from '@app/v2/event/services/event.service'
import { INVITATION_STATUS } from '@app/v2/settings/enums/invitation-status.enum'
import { OrganizationService } from '@app/v2/settings/services/organization.service'
import { OrganizationUser, USER_ROLES } from '@app/v2/shared/types/user.type'
import { AuthService } from '@auth/services/auth.service'
import { CrewMembers } from '@events/models/crew-member.interface'
import { ServerRepresentedEvent } from '@events/models/event.model'
import { EventService } from '@events/services/event.service'
import { NbSpinnerModule } from '@nebular/theme'
import { forkJoin, Observable, of, Subject } from 'rxjs'
import { expand, map, reduce, takeUntil } from 'rxjs/operators'
import { CrewService } from '../../services/crew.service'
import { InviteHeaderComponent } from './components/invite-header/invite-header.component'
import { InviteListComponent } from './components/invite-list/invite-list.component'
import { InviteSearchComponent } from './components/invite-search/invite-search.component'
import { MarketplaceComponent } from './components/marketplace/marketplace.component'

/**
 * Main crew invitation component
 */
@Component({
  selector: 'ngx-invite',
  templateUrl: './invite.component.html',
  styleUrls: ['./invite.component.scss'],
  imports: [CommonModule, InviteHeaderComponent, InviteSearchComponent, NbSpinnerModule, InviteListComponent, MarketplaceComponent],
  providers: [EventServiceV2, EventService],
  standalone: true
})
export class InviteComponent implements OnInit, OnDestroy {
  @Input() eventId?: number
  /** Subject that stops all hot observables */
  private _destroy$ = new Subject()
  /**
   * Assigned crew members list
   */
  assignedMembers: CrewMembers = []
  /**
   * Assigned crew members that are workspace members
   */
  assignedWorkspaceMembers: CrewMembers = []
  /**
   * Workspace members that are not assigned as crew members
   */
  unassignedWorkspaceMembers: Assignee[] = []
  /** On destroy implementation */
  ngOnDestroy() {
    this._destroy$.next(null)
    this._destroy$.complete()
  }
  /**
   * Creates the component and injects it's dependencies
   * @param eventService EventService
   * @param activatedRoute ActivatedRoute
   * @param service CrewService
   * @param organizationService OrganizationService
   * @param authService AuthService
   */
  constructor(
    private eventService: EventService,
    private activatedRoute: ActivatedRoute,
    private service: CrewService,
    private organizationService: OrganizationService,
    private authService: AuthService
  ) {}
  /**
   * Current event
   */
  public event: ServerRepresentedEvent
  /**
   * Whether the component is ready
   */
  public ready = false
  /**
   * Crew members list
   */
  public crew: CrewMembers = []
  /**
   * Initializes the component
   */
  ngOnInit(): void {
    this.getEventInfo()

    this.service.reloadCrewForEvent.pipe(takeUntil(this._destroy$)).subscribe(() => {
      this.ready = false
      this.init()
    })
  }
  /**
   * Gets currente vent information
   */
  getEventInfo() {
    this.eventService
      .getEvent(this.eventId || this.activatedRoute.snapshot.params.id)
      .pipe(takeUntil(this._destroy$))
      .subscribe((event: ServerRepresentedEvent) => {
        this.event = event
        this.init()
      })
  }
  /**
   * Initializes crew + workspace members (manage-team users API + existing crew API).
   */
  private init() {
    const organizationId = this.authService.currentWorkspace?.id
    if (!organizationId) return

    forkJoin([this._loadAllOrganizationUsers(organizationId), this.service.crew(this.event.id)])
      .pipe(takeUntil(this._destroy$))
      .subscribe(([organizationUsers, crew]: [OrganizationUser[], CrewMembers]) => {
        const workspaceMembers = organizationUsers
          .filter((user) => user.status === INVITATION_STATUS.ACTIVE && !!user.user_id)
          .map((user) => this._mapOrganizationUserToAssignee(user, organizationId))

        this.assignedMembers = crew
        this.assignedWorkspaceMembers = crew.filter((o) => workspaceMembers.find((u) => u.user_id === o.user_id))
        this.unassignedWorkspaceMembers = workspaceMembers.filter((o) => !this.assignedWorkspaceMembers.find((u) => u.user_id === o.user_id))
        this.crew = crew
        this.ready = true
      })
  }

  /**
   * Loads all organization users across paginated responses (same API as manage-team).
   * @param organizationId Current workspace / organization id
   */
  private _loadAllOrganizationUsers(organizationId: number): Observable<OrganizationUser[]> {
    const perPage = 100

    return this.organizationService.getOrganizationUsers(organizationId, 1, perPage).pipe(
      expand((response) => (response.current_page < response.last_page ? this.organizationService.getOrganizationUsers(organizationId, response.current_page + 1, perPage) : of())),
      map((response) => response.data ?? []),
      reduce((all, pageUsers) => all.concat(pageUsers), [] as OrganizationUser[])
    )
  }

  /**
   * Maps a manage-team OrganizationUser into the Assignee shape expected by invite cards.
   * @param user Organization user from the members API
   * @param workspaceId Current workspace id
   */
  private _mapOrganizationUserToAssignee(user: OrganizationUser, workspaceId: number): Assignee {
    return {
      id: user.id,
      user_id: user.user_id,
      workspace_id: workspaceId,
      is_owner: user.role === USER_ROLES.OWNER,
      role: user.role,
      user: {
        id: user.user_id,
        email: user.email,
        full_name: user.fullname,
        profile_picture: user.profile_picture
      } as Assignee['user'] & { full_name?: string; profile_picture?: string }
    }
  }
}
